import { useEffect, useRef, useState } from 'react';
import { PLAYER_MAX_HEALTH, ROUNDS_TO_WIN, ROUND_DRAW } from '@game/shared';
import { cn } from '../lib/cn.js';
import { useGameStore, type KillFeedEntry } from '../game/store.js';
import { TOUCH_MODE } from '../game/touch.js';
import { useSettingsStore } from '../game/settings.js';
import { Scoreboard } from './scoreboard.jsx';

/**
 * Imperative handles the engine pokes directly. Hit markers and the damage
 * vignette fire many times per second and must not go through React state.
 */
export interface HudHandles {
  hitMarker: HTMLDivElement | null;
  damageFlash: HTMLDivElement | null;
}

const Crosshair = ({ spread }: { spread: number }) => (
  <div
    style={{ ['--spread' as string]: `${spread}px` }}
    className="pointer-events-none absolute left-1/2 top-1/2 size-10 -translate-x-1/2 -translate-y-1/2"
  >
    <span className="absolute left-1/2 top-1/2 size-0.5 -translate-x-1/2 -translate-y-1/2 bg-white/90" />
    <span className="absolute bottom-[calc(50%+var(--spread))] left-1/2 h-[7px] w-0.5 -translate-x-1/2 bg-white/85 shadow-[0_0_2px_rgba(0,0,0,0.9)]" />
    <span className="absolute left-1/2 top-[calc(50%+var(--spread))] h-[7px] w-0.5 -translate-x-1/2 bg-white/85 shadow-[0_0_2px_rgba(0,0,0,0.9)]" />
    <span className="absolute right-[calc(50%+var(--spread))] top-1/2 h-0.5 w-[7px] -translate-y-1/2 bg-white/85 shadow-[0_0_2px_rgba(0,0,0,0.9)]" />
    <span className="absolute left-[calc(50%+var(--spread))] top-1/2 h-0.5 w-[7px] -translate-y-1/2 bg-white/85 shadow-[0_0_2px_rgba(0,0,0,0.9)]" />
  </div>
);

/**
 * Round wins and the round clock, top centre. The deadline is an absolute
 * store value, so the clock ticks locally and a dropped packet cannot stall it.
 */
const MatchStrip = () => {
  const roundWins = useGameStore((state) => state.roundWins);
  const roundNumber = useGameStore((state) => state.roundNumber);
  const phaseEndsAtMs = useGameStore((state) => state.phaseEndsAtMs);
  const [remainingMs, setRemainingMs] = useState(0);

  useEffect(() => {
    if (phaseEndsAtMs === 0) {
      setRemainingMs(0);
      return;
    }
    const update = () => setRemainingMs(Math.max(0, phaseEndsAtMs - performance.now()));
    update();
    const timer = window.setInterval(update, 250);
    return () => window.clearInterval(timer);
  }, [phaseEndsAtMs]);

  const totalSeconds = Math.ceil(remainingMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return (
    <div className="absolute left-1/2 top-4 -translate-x-1/2 rounded-lg bg-surface-900/70 px-4 py-1.5 text-center [text-shadow:0_2px_6px_rgba(0,0,0,0.8)]">
      <div className="flex items-center gap-3">
        <span className="min-w-[2ch] text-center text-2xl font-bold text-team-blue">
          {roundWins[0]}
        </span>
        <span
          className={cn(
            'min-w-[5ch] text-center text-lg font-semibold',
            totalSeconds <= 15 ? 'text-team-red' : 'text-ink-muted',
          )}
        >
          {phaseEndsAtMs > 0 ? `${minutes}:${String(seconds).padStart(2, '0')}` : '—'}
        </span>
        <span className="min-w-[2ch] text-center text-2xl font-bold text-team-red">
          {roundWins[1]}
        </span>
      </div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-ink-muted">
        Round {roundNumber} · first to {ROUNDS_TO_WIN}
      </div>
    </div>
  );
};

/**
 * The between-rounds card: who took the round and the running tally. Rendered
 * over the frozen arena for the few seconds of `roundEnd` — the next round
 * starts by itself, so there is nothing to click and the pointer stays locked.
 */
const RoundBanner = () => {
  const roundWins = useGameStore((state) => state.roundWins);
  const lastRoundResult = useGameStore((state) => state.lastRoundResult);

  const draw = lastRoundResult === ROUND_DRAW;
  return (
    <div className="absolute left-1/2 top-[30%] -translate-x-1/2 animate-rise text-center">
      <div
        className={cn(
          'text-5xl font-extrabold tracking-tight [text-shadow:0_4px_16px_rgba(0,0,0,0.7)]',
          draw ? 'text-ink' : lastRoundResult === 0 ? 'text-team-blue' : 'text-team-red',
        )}
      >
        {draw
          ? 'Round draw'
          : `${lastRoundResult === 0 ? 'Blue' : 'Red'} takes the round`}
      </div>
      {draw && (
        <div className="mt-1 text-sm text-ink-muted [text-shadow:0_2px_6px_rgba(0,0,0,0.8)]">
          Equal health on both sides — nobody scores
        </div>
      )}
      <div className="mt-3 flex items-center justify-center gap-4 tabular [text-shadow:0_2px_8px_rgba(0,0,0,0.8)]">
        <span className="text-4xl font-bold text-team-blue">{roundWins[0]}</span>
        <span className="text-xl text-ink-muted">—</span>
        <span className="text-4xl font-bold text-team-red">{roundWins[1]}</span>
      </div>
      <div className="mt-2 text-[11px] uppercase tracking-[0.25em] text-ink-muted [text-shadow:0_2px_6px_rgba(0,0,0,0.8)]">
        First to {ROUNDS_TO_WIN} wins the game
      </div>
    </div>
  );
};

const KillFeedRow = ({ entry }: { entry: KillFeedEntry }) => (
  <div className="animate-feed-in rounded bg-surface-900/85 px-2.5 py-1 text-sm">
    <span className={entry.attackerTeam === 0 ? 'text-team-blue' : 'text-team-red'}>
      {entry.attacker}
    </span>
    <span className={cn('mx-2', entry.headshot ? 'text-accent' : 'text-ink-muted')}>
      {entry.headshot ? '⌖' : '→'}
    </span>
    <span className={entry.victimTeam === 0 ? 'text-team-blue' : 'text-team-red'}>
      {entry.victim}
    </span>
  </div>
);

export const Hud = ({ handles }: { handles: React.RefObject<HudHandles> }) => {
  const matchPhase = useGameStore((state) => state.matchPhase);
  const health = useGameStore((state) => state.health);
  const ammo = useGameStore((state) => state.ammo);
  const magazine = useGameStore((state) => state.magazine);
  const weaponName = useGameStore((state) => state.weaponName);
  const reloading = useGameStore((state) => state.reloading);
  const alive = useGameStore((state) => state.alive);
  const spectatingName = useGameStore((state) => state.spectatingName);
  const kills = useGameStore((state) => state.kills);
  const deaths = useGameStore((state) => state.deaths);
  const killFeed = useGameStore((state) => state.killFeed);
  const pingMs = useGameStore((state) => state.pingMs);
  const fps = useGameStore((state) => state.fps);
  const scoreboardOpen = useGameStore((state) => state.scoreboardOpen);
  const crosshairSpread = useGameStore((state) => state.crosshairSpread);
  const showStats = useSettingsStore((state) => state.showStats);

  const hitMarker = useRef<HTMLDivElement>(null);
  const damageFlash = useRef<HTMLDivElement>(null);

  useEffect(() => {
    handles.current = {
      hitMarker: hitMarker.current,
      damageFlash: damageFlash.current,
    };
  }, [handles]);

  const healthPercent = Math.max(0, (health / PLAYER_MAX_HEALTH) * 100);

  return (
    <div className="pointer-events-none fixed inset-0 tabular">
      <Crosshair spread={crosshairSpread} />
      <MatchStrip />
      {matchPhase === 'roundEnd' && <RoundBanner />}

      <div
        ref={hitMarker}
        className="absolute left-1/2 top-1/2 size-[22px] -translate-x-1/2 -translate-y-1/2 opacity-0 transition-opacity duration-100 [background:linear-gradient(45deg,transparent_45%,#fff_45%,#fff_55%,transparent_55%),linear-gradient(-45deg,transparent_45%,#fff_45%,#fff_55%,transparent_55%)] data-[show=true]:opacity-100 data-[show=true]:transition-none data-[head=true]:scale-125 data-[head=true]:[background:linear-gradient(45deg,transparent_45%,#ffd34d_45%,#ffd34d_55%,transparent_55%),linear-gradient(-45deg,transparent_45%,#ffd34d_45%,#ffd34d_55%,transparent_55%)] data-[kill=true]:[background:linear-gradient(45deg,transparent_45%,var(--color-team-red)_45%,var(--color-team-red)_55%,transparent_55%),linear-gradient(-45deg,transparent_45%,var(--color-team-red)_45%,var(--color-team-red)_55%,transparent_55%)]"
      />

      <div
        ref={damageFlash}
        className="absolute inset-0 opacity-0 transition-opacity duration-[400ms] [background:radial-gradient(circle_at_50%_50%,transparent_45%,rgba(220,40,40,0.55)_100%)] data-[show=true]:opacity-100 data-[show=true]:transition-none"
      />

      <div
        className={cn(
          'absolute bottom-0 left-0 flex items-center gap-3 p-6 [text-shadow:0_2px_6px_rgba(0,0,0,0.8)]',
          // Clear of the thumbstick zone and the fire cluster on touch.
          TOUCH_MODE && 'bottom-28',
        )}
      >
        <div className="h-2.5 w-56 overflow-hidden rounded-sm bg-white/15">
          <div
            className="h-full bg-gradient-to-r from-health to-lime-300 transition-[width] duration-100"
            style={{ width: `${healthPercent}%` }}
          />
        </div>
        <div className="min-w-[3ch] text-3xl font-bold">{health}</div>
      </div>

      <div
        className={cn(
          'absolute bottom-0 right-0 p-6 text-right [text-shadow:0_2px_6px_rgba(0,0,0,0.8)]',
          TOUCH_MODE && 'right-44',
        )}
      >
        <div
          className={cn(
            'text-4xl font-bold tracking-tight',
            reloading && 'text-accent',
          )}
        >
          {ammo}
          <span className="mx-1 text-2xl opacity-40">/</span>
          <span>{magazine}</span>
        </div>
        <div className="text-[13px] uppercase tracking-[0.2em] text-ink-muted">
          {reloading ? 'Reloading' : weaponName}
        </div>
      </div>

      <div
        className={cn(
          'absolute left-0 top-0 flex gap-5 p-6 [text-shadow:0_2px_6px_rgba(0,0,0,0.8)]',
          // The touch pause/scoreboard buttons live in that corner.
          TOUCH_MODE && 'left-20',
        )}
      >
        <div className="flex items-baseline gap-1.5 text-xl font-semibold">
          <span className="text-[11px] tracking-[0.2em] text-ink-muted">K</span>
          {kills}
        </div>
        <div className="flex items-baseline gap-1.5 text-xl font-semibold">
          <span className="text-[11px] tracking-[0.2em] text-ink-muted">D</span>
          {deaths}
        </div>
      </div>

      {showStats && (
        <div className="absolute right-0 top-0 p-6 text-[13px] text-ink-muted">
          {pingMs} ms · {fps} fps
        </div>
      )}

      <div className="absolute right-6 top-16 flex flex-col items-end gap-1.5">
        {killFeed.map((entry) => (
          <KillFeedRow key={entry.id} entry={entry} />
        ))}
      </div>

      {!alive && matchPhase === 'live' && (
        <div className="absolute left-1/2 top-[58%] -translate-x-1/2 text-center">
          <div className="text-2xl font-bold uppercase tracking-[0.25em] text-team-red [text-shadow:0_2px_8px_rgba(0,0,0,0.8)]">
            Eliminated
          </div>
          {spectatingName ? (
            <div className="mt-1 text-sm [text-shadow:0_2px_6px_rgba(0,0,0,0.8)]">
              Watching <span className="font-semibold">{spectatingName}</span>
              <span className="text-ink-muted">
                {TOUCH_MODE
                  ? ' · fire for next · joystick for free cam'
                  : ' · click for next · WASD for free cam'}
              </span>
            </div>
          ) : (
            <div className="mt-1 text-sm text-ink-muted [text-shadow:0_2px_6px_rgba(0,0,0,0.8)]">
              {TOUCH_MODE
                ? "You're back when the round ends — tap fire to watch a teammate"
                : "You're back when the round ends — click to watch a teammate"}
            </div>
          )}
        </div>
      )}

      {scoreboardOpen && <Scoreboard />}
    </div>
  );
};
